import React, { useMemo, useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { CustomerLayout } from "@/components/Layout/CustomerLayout";
import { useCart } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import { useCurrency } from "@/contexts/CurrencyContext";
import api from "@/lib/api";
import { Banknote, CreditCard, Wallet as WalletIcon, Loader2, Smartphone } from "lucide-react";

export default function Checkout() {
  const { cart, subtotal, clear } = useCart();
  const { user, refreshUser } = useAuth();
  const { t, lang } = useLang();
  const { formatPrice } = useCurrency();
  const navigate = useNavigate();
  const [address, setAddress] = useState("Al Mogran, Khartoum");
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const deliveryFee = 2.5;
  const tax = useMemo(() => +(subtotal * 0.05).toFixed(2), [subtotal]);
  const total = useMemo(() => +(subtotal + deliveryFee + tax).toFixed(2), [subtotal, tax]);

  const submittedRef = useRef(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setErr("");
    try {
      const { data } = await api.post("/orders/checkout", {
        restaurant_id: cart.restaurantId,
        items: cart.items.map(i => ({ menu_item_id: i.id, quantity: i.quantity })),
        address,
        payment_method: paymentMethod,
        notes,
      });
      submittedRef.current = true;
      navigate(`/app/orders/${data.id}`, { replace: true });
      clear();
      refreshUser();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Checkout failed");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (!submittedRef.current && cart.items.length === 0) {
      navigate("/app/cart", { replace: true });
    }
  }, [cart.items.length, navigate]);

  const methods = [
    { v: "cash", label: t("cash"), icon: Banknote, enabled: true },
    { v: "wallet", label: `${t("walletPay")} (${formatPrice(user?.wallet_balance || 0)})`, icon: WalletIcon, enabled: true },
    { v: "mbok", label: "mBOK", icon: Smartphone, enabled: false, badge: "🇸🇩", note: lang === "ar" ? "قريباً — بانتظار المفاتيح" : "Coming soon — awaiting API keys" },
    { v: "mpay", label: "mPay", icon: Smartphone, enabled: false, badge: "🇸🇩", note: lang === "ar" ? "قريباً — بانتظار المفاتيح" : "Coming soon — awaiting API keys" },
    { v: "card", label: t("card"), icon: CreditCard, enabled: false, note: lang === "ar" ? "قريباً — قيد التكامل مع البنك" : "Coming soon — payment gateway integration in progress" },
  ];

  return (
    <CustomerLayout>
      <form onSubmit={submit} className="max-w-2xl mx-auto px-4 py-6" data-testid="checkout-form">
        <h1 className="text-2xl font-bold">{t("checkout")}</h1>

        <div className="mt-5 so-card p-5">
          <label className="text-xs font-bold uppercase tracking-wider text-[var(--so-text-soft)]">{t("deliverTo")}</label>
          <input className="so-input mt-2" value={address} onChange={e => setAddress(e.target.value)} required data-testid="checkout-address" />
          <textarea
            className="so-input mt-3"
            rows={2}
            placeholder="Notes for the restaurant or rider..."
            value={notes}
            onChange={e => setNotes(e.target.value)}
            data-testid="checkout-notes"
          />
        </div>

        <div className="mt-5 so-card p-5">
          <div className="text-xs font-bold uppercase tracking-wider text-[var(--so-text-soft)] mb-3">{t("paymentMethod")}</div>
          <div className="space-y-2">
            {methods.map(({ v, label, icon: Icon, enabled, badge, note }) => (
              <button
                type="button"
                key={v}
                onClick={() => enabled && setPaymentMethod(v)}
                disabled={!enabled}
                data-testid={`pay-${v}`}
                className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 transition-all ${paymentMethod === v ? "border-[var(--so-orange)] bg-[var(--so-orange)]/5" : "border-[var(--so-border)]"} ${!enabled ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <Icon className={`w-5 h-5 ${paymentMethod === v ? "text-[var(--so-orange)]" : ""}`} />
                <div className="flex-1 text-start">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-sm">{label}</span>
                    {badge && <span className="text-xs">{badge}</span>}
                  </div>
                  {note && <div className="text-[10px] text-[var(--so-text-soft)] mt-0.5">{note}</div>}
                </div>
                {!enabled && (
                  <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-700 dark:text-amber-400">
                    {lang === "ar" ? "قريباً" : "Soon"}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-5 so-card p-5 space-y-2 text-sm">
          <Row label={t("subtotal")} value={formatPrice(subtotal)} />
          <Row label={t("deliveryFee")} value={formatPrice(deliveryFee)} />
          <Row label={t("tax")} value={formatPrice(tax)} />
          <div className="border-t border-[var(--so-border)] pt-2 mt-2 flex justify-between font-bold text-base">
            <span>{t("total")}</span>
            <span className="text-[var(--so-orange)]" data-testid="checkout-total">{formatPrice(total)}</span>
          </div>
        </div>

        {err && <div className="mt-4 text-sm text-red-500 bg-red-500/10 border border-red-500/20 rounded-lg p-3" data-testid="checkout-error">{String(err)}</div>}

        <button type="submit" disabled={loading} className="so-btn-primary w-full justify-center mt-5" data-testid="place-order">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : `${t("placeOrder")} · ${formatPrice(total)}`}
        </button>
      </form>
    </CustomerLayout>
  );
}

const Row = ({ label, value }) => (
  <div className="flex justify-between">
    <span className="text-[var(--so-text-soft)]">{label}</span>
    <span className="font-semibold">{value}</span>
  </div>
);
